import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { searchResponseResource } from "../../../../../resources/js/search/proptypes/main-search-result-resource";
import Skeleton from "react-loading-skeleton";
import SearchErrorHandler from "./SearchErrorHandler";
import SearchResultCard from "../card/SearchResultCard";

const SearchResultsList = (props) => {
    const {
        isLoading,
        results,
        isSelectedTypesEmpty
    } = props;

    const isResultEmpty = useMemo(() => {
        return !Object.values(results).some(value => Array.isArray(value) && value.length > 0);
    }, [results]);

    const loadingComponent = useMemo(() => {
        return Array(6).fill().map((_, index) => (
            <div className="w-100 px-2 pb-1 d-inline-block text-truncate" key={index}>
                <div className="rounded clickable" style={{ backgroundColor: "#2d3237" }}>
                    <div className="d-flex">
                        <div className="position-relative align-content-center">
                            <Skeleton circle={true} height={44} width={44} />
                        </div>
                        <div className="flex-grow-1 overflow-auto ps-1 small">
                            <p className="text-truncate fw-bold mb-0">
                                <Skeleton width={200} />
                            </p>
                            <span className="text-muted" style={{ fontSize: "10px" }}><Skeleton width={150} /></span>
                        </div>
                    </div>
                </div>
            </div>
        ));
    }, []);

    const noTypesSelectedComponent = useMemo(() => (
        <SearchErrorHandler>
            <p>Please try searching with a specific category</p>
        </SearchErrorHandler>
    ), []);

    const noResultComponent = useMemo(() => (
        <SearchErrorHandler>
            <p>Please try using a different keyword for your search</p>
        </SearchErrorHandler>
    ), []);

    if (isLoading) {
        return loadingComponent;
    }

    if (isSelectedTypesEmpty) {
        return noTypesSelectedComponent;
    }

    if (isResultEmpty) {
        return noResultComponent;
    }

    return (
        <div>
            {results.states && results.states.map(state => (
                <SearchResultCard
                    key={`search-result-state-${state.id}`}
                    title={`${state.name}, ${state.description}`}
                    show_route={state.show_route}
                    isCenter={true}
                />
            ))}

            {results.counties && results.counties.map(county => (
                <SearchResultCard
                    key={`search-result-county-${county.id}`}
                    title={`${county.name}, ${county.description}`}
                    show_route={county.show_route}
                    isCenter={true}
                />
            ))}

            {results.subcounties && results.subcounties.map(subcounty => (
                <SearchResultCard
                    key={`search-result-subcounty-${subcounty.id}`}
                    title={`${subcounty.name}, ${subcounty.description}`}
                    show_route={subcounty.show_route}
                    isCenter={true}
                />
            ))}

            {results.users && results.users.map(user => (
                <SearchResultCard
                    key={`search-result-user-${user.id}`}
                    title={user.name}
                    level={user.level}
                    icon={"fas fa-user fa-fw"}
                    badge={true}
                    show_route={user.show_route}
                    image={user.image}
                />
            ))}

            {results.albums && results.albums.map(album => (
                <SearchResultCard
                    key={`search-result-album-${album.id}`}
                    title={album.name}
                    icon={"fas fa-solid fa-images fa-fw"}
                    show_route={album.show_route}
                    image={album.image}
                    additionalInfo={album.description}
                />
            ))}

            {results.sites && results.sites.map(site => (
                <SearchResultCard key={`search-result-site-${site.id}`}
                    title={site.name}
                    level={site.level}
                    icon={"fa-solid fa-map-pin fa-fw"}
                    badge={true}
                    show_route={site.show_route}
                    image={site.image}
                    additionalInfo={<> • &nbsp;<i className="fa-solid fa-earth-americas fa-fw"></i> {site.description}</>}
                />
            ))}

            {results.countries && results.countries.map(country => (
                <SearchResultCard
                    key={`search-result-country-${country.id}`}
                    title={country.name}
                    show_route={country.show_route}
                    item={country}
                    isCenter={true}
                />
            ))}
        </div>
    );
};

SearchResultsList.propTypes = {
    isLoading: PropTypes.bool,
    results: searchResponseResource,
    isSelectedTypesEmpty: PropTypes.bool,
};

SearchResultsList.defaultProps = {
    isLoading: false,
    isSelectedTypesEmpty: false,
}

export default SearchResultsList;
